import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarClock,
  Check,
  ChevronDown,
  Loader2,
  MapPin,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { Kicker, SmchsFooter, SmchsNav, SmchsStyleBlock } from '@/components/smchs/Shell';
import {
  BookCover,
  CollectionStyle,
  GrainOverlay,
  Reveal,
} from '@/components/smchs/Collection';
import { ReplyComposer, ReplyList } from '@/components/smchs/Discussion';
import { discussionKeys, flaggedNotice, timeAgo } from '@/components/smchs/discussion-lib';
import { authorLine } from '@/components/smchs/collection-lib';
import { resolveCoverImageUrl } from '@/lib/cover-image';
import { useAuth } from '@/hooks/useAuth';
import { useMyLoanSummary } from '@/hooks/student-loans-hooks';
import {
  useCancelBorrowRequest,
  useCreateBorrowRequest,
  useMyBorrowRequests,
  useRenewLoanGeneric,
} from '@/hooks/loans-hooks';
import { useMarkAllAsRead, useNotifications } from '@/hooks/notifications-hooks';
import { useMyProfile, useUploadMyAvatar } from '@/hooks/user-hooks';
import { booksApi, type Book } from '@/services/catalog-api';
import {
  bookclubClubsApi,
  bookclubDiscussionsApi,
  bookclubMeetingsApi,
  bookclubMembershipApi,
  type ClubMeeting,
  type DiscussionThread,
  type RSVPStatus,
} from '@/services/bookclub-api';
import { challengesApi, type Challenge } from '@/services/challenges-api';
import type { BorrowRequest, Loan } from '@/services/loans-api';

/* =====================================================================
 * Student desk: the signed-in reader's page in the "living collection"
 * vocabulary (see Index.tsx / Collection.tsx). Every figure comes from
 * the API: loan summary, borrow requests, notifications, challenge
 * enrolment. Sections with nothing to say render an explicit quiet
 * state or, where the data is genuinely absent, nothing at all.
 *
 * Radius rule: panels/covers rounded-md, controls rounded-lg, chips
 * rounded-full. Mono small-caps only for data metadata.
 * ===================================================================== */

const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

/* The security error handler collapses most 4xx bodies to
   {error: {code, message}}; explicit view errors send {error: "..."}. */
const apiError = (e: unknown, fallback: string): string => {
  const data = (e as { response?: { data?: { error?: unknown; message?: unknown } } })?.response
    ?.data;
  if (typeof data?.error === 'string') return data.error;
  const nested = (data?.error as { message?: unknown } | undefined)?.message;
  if (typeof nested === 'string') return nested;
  if (typeof data?.message === 'string') return data.message;
  return fallback;
};

/* BookCover wants a catalog Book; loans carry the same facts under
   copy-info names. Adapter, not a fetch: no extra round trip. */
const loanBook = (l: Loan): Book =>
  ({
    id: l.book_copy,
    title: l.book_copy_info?.book_title || 'Untitled',
    isbn: l.book_copy_info?.book_isbn || '',
    cover_image: l.book_copy_info?.book_cover_image || null,
    author_names: (l.book_copy_info?.book_authors || []).join(', '),
  }) as unknown as Book;

/* ===================================================================== */

const StudentDesk: React.FC = () => (
  <div className="smchs-home">
    <SmchsStyleBlock />
    <CollectionStyle />
    <SmchsNav />
    <main className="relative">
      <DeskBand />
      <DeskSpread />
      <FindNextBand />
      <ClubsSection />
      <DiscussionsSection />
      <ChallengesStrip />
    </main>
    <SmchsFooter />
    <GrainOverlay />
  </div>
);

/* =====================================================================
 * DESK BAND: greeting, card metadata, and the ledger line. The ledger
 * deliberately shows zeros: "nothing out, nothing owed" is account
 * state a reader wants confirmed, unlike public stats.
 * ===================================================================== */

const DeskBand: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const { data: summary, isLoading } = useMyLoanSummary();
  const { data: myRequests } = useMyBorrowRequests();

  const firstName = user?.first_name || user?.username || 'Reader';
  const hour = new Date().getHours();
  const daypart = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const loans = summary?.loans ?? [];
  const activeCount = summary?.active_count ?? 0;
  const pendingCount = (myRequests?.results ?? []).filter((r) => r.status === 'pending').length;
  const owed = summary?.outstanding_fines_total ? parseFloat(summary.outstanding_fines_total) : 0;
  const overdueCount = loans.filter((l) => l.is_overdue).length;
  const nextDue = loans
    .filter((l) => !l.is_overdue)
    .map((l) => l.due_date)
    .sort()[0];

  const onSignOut = async () => {
    setSigningOut(true);
    try {
      await logout();
      navigate('/');
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <section className="relative overflow-hidden border-b border-[var(--border)]">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(30,45,92,0.12),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:56px_56px] [mask-image:radial-gradient(ellipse_60%_80%_at_50%_0%,black,transparent_70%)] opacity-40" />
      </div>
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-10 md:pb-12">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <Kicker label="Student desk" />
            <h1 className="mt-4 pb-1 font-display font-[900] text-[2.2rem] sm:text-[2.9rem] md:text-[3.4rem] leading-[1.08] tracking-[-0.03em] text-[var(--ink)]">
              Good {daypart},{' '}
              <span className="font-serif italic font-normal text-[var(--primary)]">
                {firstName}
              </span>
              .
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
              <span>{today}</span>
              {user?.student_id && (
                <>
                  <span aria-hidden className="w-px h-3 bg-[var(--border)]" />
                  <span>Card {user.student_id}</span>
                </>
              )}
              <span aria-hidden className="w-px h-3 bg-[var(--border)]" />
              <button
                onClick={onSignOut}
                disabled={signingOut}
                className="uppercase tracking-[0.15em] text-[var(--muted)] hover:text-[var(--ink)] transition-colors disabled:opacity-50"
              >
                {signingOut ? 'Signing out' : 'Sign out'}
              </button>
            </div>
          </div>
          <DeskIdentity fallbackInitial={firstName.charAt(0)} />
        </div>

        {/* Ledger line */}
        <div className="mt-10 border-y border-[var(--border)] py-5 flex flex-wrap items-center gap-y-5">
          <LedgerFigure
            label="Books out"
            value={isLoading ? null : String(activeCount).padStart(2, '0')}
          />
          <span aria-hidden className="hidden sm:block w-px self-stretch bg-[var(--border)] mx-7" />
          <LedgerFigure
            label="On request"
            value={isLoading ? null : String(pendingCount).padStart(2, '0')}
          />
          <span aria-hidden className="hidden sm:block w-px self-stretch bg-[var(--border)] mx-7" />
          <LedgerFigure label="Owed" value={isLoading ? null : `$${owed.toFixed(2)}`} danger={owed > 0} />
          <div className="ml-auto font-mono text-[11.5px] uppercase tracking-[0.15em] w-full sm:w-auto">
            {isLoading ? (
              <span className="inline-block h-4 w-36 rounded bg-[var(--surface-2)] animate-pulse" />
            ) : overdueCount > 0 ? (
              <span className="text-[var(--danger)]">
                {overdueCount} overdue, return soon
              </span>
            ) : nextDue ? (
              <span className="text-[var(--muted)]">Next return {shortDate(nextDue)}</span>
            ) : (
              <span className="text-[var(--muted)]">Nothing due</span>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const LedgerFigure: React.FC<{ label: string; value: string | null; danger?: boolean }> = ({
  label,
  value,
  danger,
}) => (
  <div className="mr-7 sm:mr-0">
    {value === null ? (
      <span className="block h-9 w-14 rounded bg-[var(--surface-2)] animate-pulse" />
    ) : (
      <span
        className={`block font-display font-[900] text-[1.9rem] leading-none tracking-[-0.02em] tabular-nums ${
          danger ? 'text-[var(--danger)]' : 'text-[var(--ink)]'
        }`}
      >
        {value}
      </span>
    )}
    <span className="mt-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
      {label}
    </span>
  </div>
);

/* Card photo with self-service upload. Kept from the old page; the
   backend accepts multipart {avatar} via the profile endpoint. */
const DeskIdentity: React.FC<{ fallbackInitial: string }> = ({ fallbackInitial }) => {
  const { data: profile } = useMyProfile();
  const upload = useUploadMyAvatar();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const p = profile as { avatar?: string; user?: { avatar?: string; image?: string } } | undefined;
  const stored = p?.avatar || p?.user?.avatar || p?.user?.image || null;
  const src = preview || resolveCoverImageUrl(stored);

  const handleFile = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Images need to stay under 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setPreview(String(reader.result));
    reader.readAsDataURL(file);
    const fd = new FormData();
    fd.append('avatar', file);
    try {
      await upload.mutateAsync(fd);
      toast.success('Card photo updated');
      setPreview(null);
    } catch (e) {
      setPreview(null);
      toast.error(apiError(e, 'Could not update the photo'));
    }
  };

  return (
    <div className="shrink-0">
      <button
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        aria-label="Change card photo"
        className={`group relative w-16 h-16 rounded-full overflow-hidden border border-[var(--border)] bg-[var(--primary)] text-[var(--paper)] flex items-center justify-center transition-opacity ${
          upload.isPending ? 'animate-pulse' : ''
        }`}
      >
        {src ? (
          <img src={src} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <span className="font-display font-[900] text-[1.4rem]">{fallbackInitial}</span>
        )}
        <span className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-black/55 to-transparent opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
          <span className="pb-1.5 font-mono text-[8.5px] uppercase tracking-[0.18em] text-white">
            Edit
          </span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};

/* =====================================================================
 * DESK SPREAD: checked-out shelf on the left, the paperwork rail
 * (requests, account slip, notices) on the right.
 * ===================================================================== */

const DeskSpread: React.FC = () => (
  <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-12 md:pt-16 pb-2">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
      <div className="lg:col-span-7">
        <CheckedOutPanel />
      </div>
      <div className="lg:col-span-5 space-y-5">
        <RequestsPanel />
        <AccountSlip />
        <NoticesPanel />
      </div>
    </div>
  </section>
);

const Panel: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className = '',
  children,
}) => (
  <Reveal className={className}>
    <div className="h-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-6 md:p-7">
      {children}
    </div>
  </Reveal>
);

const PanelTitle: React.FC<{ children: React.ReactNode; meta?: React.ReactNode }> = ({
  children,
  meta,
}) => (
  <div className="flex items-baseline justify-between gap-3 mb-5">
    <span className="text-[14.5px] font-semibold text-[var(--ink)]">{children}</span>
    {meta}
  </div>
);

const quietButton =
  'px-3 py-1.5 rounded-lg border border-[var(--border)] text-[12.5px] font-medium text-[var(--ink)] hover:border-[var(--primary)] hover:text-[var(--primary)] active:translate-y-px transition-all disabled:opacity-40 disabled:pointer-events-none';

/* Renewal is self-service; returning is not. A return records a physical
   custody transfer (it closes the loan, stops the fine, and puts the copy
   back on the shelf), so it is recorded at the desk when the book is handed
   over. The endpoint is librarian-gated to match. */
const CheckedOutPanel: React.FC = () => {
  const { data: summary, isLoading, isError } = useMyLoanSummary();
  const renewLoan = useRenewLoanGeneric();
  const [renewingId, setRenewingId] = useState<number | null>(null);

  const loans = summary?.loans ?? [];

  const onRenew = async (loan: Loan) => {
    setRenewingId(loan.id);
    try {
      await renewLoan.mutateAsync({ id: loan.id, reason: 'Student self-renewal' });
      toast.success(`Renewed ${loan.book_copy_info?.book_title || 'the loan'}`);
    } catch (e) {
      toast.error(apiError(e, 'Could not renew this loan'));
    } finally {
      setRenewingId(null);
    }
  };

  return (
    <Panel>
      <PanelTitle
        meta={
          loans.length > 0 ? (
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
              {loans.length} out
            </span>
          ) : undefined
        }
      >
        Checked out
      </PanelTitle>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex gap-4">
              <div className="w-11 aspect-[2/3] rounded bg-[var(--surface-2)] animate-pulse shrink-0" />
              <div className="flex-1 space-y-2 pt-1">
                <div className="h-4 w-3/4 rounded bg-[var(--surface-2)] animate-pulse" />
                <div className="h-3 w-1/2 rounded bg-[var(--surface-2)] animate-pulse" />
                <div className="h-3 w-1/3 rounded bg-[var(--surface-2)] animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="text-[14px] text-[var(--danger)] py-2">
          Your loans would not load. Refresh the page to try again.
        </p>
      ) : loans.length === 0 ? (
        <div className="py-2">
          <p className="text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[38ch]">
            Nothing on your desk right now. The collection is open.
          </p>
          <Link
            to="/catalog"
            className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
          >
            Browse the catalog <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
          </Link>
        </div>
      ) : (
        <div>
          {loans.map((l) => {
            const renewBusy = renewingId === l.id;
            const canRenew = l.can_renew_status?.can_renew !== false;
            return (
              <div
                key={l.id}
                className="flex gap-4 py-4 border-b border-[var(--border)] last:border-0 last:pb-1 first:pt-0"
              >
                <BookCover book={loanBook(l)} className="w-11 aspect-[2/3] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[15px] leading-snug text-[var(--ink)]">
                    {l.book_copy_info?.book_title || 'Untitled'}
                  </div>
                  <div className="mt-0.5 text-[12.5px] text-[var(--ink-soft)] truncate">
                    {(l.book_copy_info?.book_authors || []).join(', ') || 'Unknown author'}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-[11.5px]">
                    <span
                      className={`inline-flex items-center gap-1.5 ${
                        l.is_overdue ? 'text-[var(--danger)]' : 'text-[var(--muted)]'
                      }`}
                    >
                      <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.75} />
                      {l.is_overdue
                        ? `${l.days_overdue}d overdue`
                        : `Due ${shortDate(l.due_date)}`}
                    </span>
                    {l.renewed_count > 0 && (
                      <span className="text-[var(--muted)]">Renewed {l.renewed_count}x</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 self-center">
                  <button
                    onClick={() => onRenew(l)}
                    disabled={!canRenew || renewBusy}
                    title={!canRenew ? l.can_renew_status?.reason : undefined}
                    className={quietButton}
                  >
                    {renewBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
                    ) : (
                      'Renew'
                    )}
                  </button>
                </div>
              </div>
            );
          })}
          <p className="mt-5 text-[12.5px] leading-[1.55] text-[var(--muted)]">
            Bring books to the library desk to return them. A librarian checks each one
            back in.
          </p>
        </div>
      )}
    </Panel>
  );
};

/* Status vocabulary for borrow requests: a word plus a dot, never
   colour alone. */
const REQUEST_STATUS: Record<
  BorrowRequest['status'],
  { word: string; dot: string; text: string }
> = {
  pending: { word: 'Waiting', dot: 'bg-[var(--gold)]', text: 'text-[var(--gold-text)]' },
  fulfilled: { word: 'Fulfilled', dot: 'bg-[var(--success)]', text: 'text-[var(--success)]' },
  rejected: { word: 'Declined', dot: 'bg-[var(--danger)]', text: 'text-[var(--danger)]' },
  cancelled: { word: 'Cancelled', dot: 'bg-[var(--border)]', text: 'text-[var(--muted)]' },
};

const RequestsPanel: React.FC = () => {
  const { data, isLoading } = useMyBorrowRequests();
  const cancelRequest = useCancelBorrowRequest();
  const [cancellingId, setCancellingId] = useState<number | null>(null);

  const requests = data?.results ?? [];
  const pending = requests.filter((r) => r.status === 'pending');
  const settled = requests.filter((r) => r.status !== 'pending').slice(0, 3);

  const onCancel = async (r: BorrowRequest) => {
    setCancellingId(r.id);
    try {
      await cancelRequest.mutateAsync(r.id);
      toast.success('Request cancelled');
    } catch (e) {
      toast.error(apiError(e, 'Could not cancel this request'));
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <Panel>
      <PanelTitle
        meta={
          pending.length > 0 ? (
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
              {pending.length} waiting
            </span>
          ) : undefined
        }
      >
        Borrow requests
      </PanelTitle>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-10 rounded bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      ) : requests.length === 0 ? (
        <p className="text-[14px] text-[var(--ink-soft)] py-1">
          No requests in flight. Ask for a book from the search below.
        </p>
      ) : (
        <div>
          {pending.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 py-3 border-b border-[var(--border)] last:border-0"
            >
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${REQUEST_STATUS.pending.dot}`} />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium truncate text-[var(--ink)]">
                  {r.book_title || 'Untitled'}
                </div>
                <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--muted)] mt-0.5">
                  Since {shortDate(r.created_at)}
                </div>
              </div>
              <button
                onClick={() => onCancel(r)}
                disabled={cancellingId === r.id}
                className={quietButton}
              >
                {cancellingId === r.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
                ) : (
                  'Cancel'
                )}
              </button>
            </div>
          ))}
          {settled.map((r) => {
            const s = REQUEST_STATUS[r.status];
            return (
              <div
                key={r.id}
                className="flex items-start gap-3 py-3 border-b border-[var(--border)] last:border-0"
              >
                <span className={`mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 ${s.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[14px] truncate text-[var(--ink-soft)]">
                    {r.book_title || 'Untitled'}
                  </div>
                  {r.status === 'rejected' && (r.rejection_reason || r.notes) && (
                    <div className="text-[12px] text-[var(--danger)] mt-0.5">
                      {r.rejection_reason || r.notes}
                    </div>
                  )}
                </div>
                <span
                  className={`shrink-0 font-mono text-[10.5px] uppercase tracking-[0.14em] ${s.text}`}
                >
                  {s.word}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
};

/* Renders only when money is actually owed; the ledger already covers
   the zero state. Copy reflects real policy: outstanding fines block
   new loans (see LoanAPISerializer.validate_borrower). */
const AccountSlip: React.FC = () => {
  const { data: summary } = useMyLoanSummary();
  const owed = summary?.outstanding_fines_total
    ? parseFloat(summary.outstanding_fines_total)
    : 0;
  const count = summary?.outstanding_fines_count ?? 0;
  if (owed <= 0) return null;

  return (
    <Reveal>
      <div className="rounded-md border border-[var(--gold)]/40 bg-[var(--gold)]/10 p-6 md:p-7">
        <PanelTitle>Account</PanelTitle>
        <div className="font-display font-[900] text-[2rem] leading-none tracking-[-0.02em] tabular-nums text-[var(--ink)]">
          ${owed.toFixed(2)}
        </div>
        <p className="mt-3 text-[13.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[36ch]">
          Outstanding across {count} {count === 1 ? 'fine' : 'fines'}. Settle at the front desk;
          new loans stay on hold until the balance clears.
        </p>
      </div>
    </Reveal>
  );
};

const NoticesPanel: React.FC = () => {
  const { data, isLoading } = useNotifications({ limit: 4 });
  const markAll = useMarkAllAsRead();
  const items = data?.results ?? [];
  const unread = items.some((n) => !n.is_read);

  const onMarkAll = async () => {
    try {
      await markAll.mutateAsync();
      toast.success('Notices cleared');
    } catch (e) {
      toast.error(apiError(e, 'Could not mark notices as read'));
    }
  };

  return (
    <Panel>
      <PanelTitle
        meta={
          unread ? (
            <button
              onClick={onMarkAll}
              disabled={markAll.isPending}
              className="text-[12px] font-medium text-[var(--ink-soft)] hover:text-[var(--primary)] transition-colors disabled:opacity-50"
            >
              {markAll.isPending ? 'Clearing' : 'Mark all read'}
            </button>
          ) : undefined
        }
      >
        Notices
      </PanelTitle>
      {isLoading ? (
        <div className="space-y-3.5">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-9 rounded bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-[14px] text-[var(--ink-soft)] py-1">All caught up.</p>
      ) : (
        <ul className="space-y-3.5">
          {items.map((n) => (
            <li key={n.id} className="flex gap-3">
              <span
                className={`mt-[7px] w-1.5 h-1.5 rounded-full shrink-0 ${
                  n.is_read ? 'bg-[var(--border)]' : 'bg-[var(--gold)]'
                }`}
              />
              <div className="min-w-0">
                <div className="text-[14px] leading-[1.45] text-[var(--ink)]">
                  {n.title || n.message}
                </div>
                <div className="text-[11px] text-[var(--muted)] font-mono uppercase tracking-[0.14em] mt-1">
                  {shortDate(n.created_at)}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};

/* =====================================================================
 * FIND YOUR NEXT: inline collection search with request-to-borrow.
 * A librarian approves each request, so the button asks, it does not
 * take.
 * ===================================================================== */

function useDebounced<T>(value: T, delay = 350): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const FindNextBand: React.FC = () => {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim());
  const { data: myRequests } = useMyBorrowRequests();
  const createRequest = useCreateBorrowRequest();
  const [requestingId, setRequestingId] = useState<number | null>(null);

  const pendingBookIds = useMemo(
    () =>
      new Set(
        (myRequests?.results ?? [])
          .filter((r) => r.status === 'pending')
          .map((r) => r.book),
      ),
    [myRequests],
  );

  const {
    data: results,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['student', 'desk', 'search', debounced],
    queryFn: () => booksApi.getBooks({ search: debounced, page_size: 6 }),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });

  const books = debounced.length >= 2 ? (results?.results ?? []) : [];

  const onRequest = async (book: Book) => {
    setRequestingId(book.id);
    try {
      await createRequest.mutateAsync({ book: book.id });
      toast.success(`Requested ${book.title}`);
    } catch (e) {
      toast.error(apiError(e, 'Could not send that request'));
    } finally {
      setRequestingId(null);
    }
  };

  return (
    <section className="mt-14 md:mt-20 border-y border-[var(--border)] bg-[var(--surface-2)]/60">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="lg:col-span-5">
            <h2 className="font-display font-[900] text-[1.8rem] md:text-[2.2rem] leading-[1.05] tracking-[-0.025em] text-[var(--ink)]">
              Find your{' '}
              <span className="font-serif italic font-normal text-[var(--primary)]">next</span>{' '}
              book.
            </h2>
            <p className="mt-4 text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[40ch]">
              Search the collection and ask to borrow. A librarian confirms each request at the
              desk.
            </p>
            <Link
              to="/catalog"
              className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
            >
              Browse the full catalog <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
            </Link>
          </Reveal>

          <Reveal delay={0.08} className="lg:col-span-7">
            <div className="relative">
              <Search
                className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--muted)]"
                strokeWidth={1.75}
              />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by title, author, or ISBN"
                aria-label="Search the collection"
                className="w-full bg-transparent pl-8 pr-2 py-3 text-[1.15rem] md:text-[1.3rem] font-display text-[var(--ink)] placeholder:text-[var(--muted)] border-0 border-b border-[var(--border)] focus:border-[var(--gold)] focus:outline-none transition-colors"
              />
            </div>

            <div className="mt-2">
              {debounced.length < 2 ? null : isFetching && books.length === 0 ? (
                <div className="divide-y divide-[var(--border)]">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex items-center gap-4 py-3.5">
                      <div className="w-9 h-[54px] rounded bg-[var(--surface)] animate-pulse shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-2/3 rounded bg-[var(--surface)] animate-pulse" />
                        <div className="h-3 w-1/3 rounded bg-[var(--surface)] animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : isError ? (
                <p className="py-4 text-[14px] text-[var(--danger)]">
                  Search is not responding right now. Try again in a moment.
                </p>
              ) : books.length === 0 ? (
                <p className="py-4 text-[14px] text-[var(--ink-soft)]">
                  Nothing in the collection matches &ldquo;{debounced}&rdquo;.
                </p>
              ) : (
                <div className="divide-y divide-[var(--border)]">
                  {books.map((b) => {
                    const requested = pendingBookIds.has(b.id);
                    const busy = requestingId === b.id;
                    const copies =
                      typeof b.available_copies === 'number' ? b.available_copies : null;
                    return (
                      <div key={b.id} className="flex items-center gap-4 py-3.5">
                        <BookCover book={b} className="w-9 aspect-[2/3] shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[14.5px] truncate text-[var(--ink)]">
                            {b.title}
                          </div>
                          <div className="text-[12.5px] text-[var(--ink-soft)] truncate">
                            {authorLine(b)}
                          </div>
                        </div>
                        {copies !== null && (
                          <span className="hidden sm:inline-flex items-center gap-1.5 shrink-0 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--muted)]">
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                copies > 0 ? 'bg-[var(--success)]' : 'bg-[var(--border)]'
                              }`}
                            />
                            {copies > 0 ? `${copies} in` : 'None in'}
                          </span>
                        )}
                        <button
                          onClick={() => onRequest(b)}
                          disabled={requested || busy}
                          className={
                            requested
                              ? 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--success)]/40 bg-[var(--success)]/10 text-[12.5px] font-medium text-[var(--success)]'
                              : quietButton
                          }
                        >
                          {busy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : requested ? (
                            <>
                              <Check className="w-3.5 h-3.5" strokeWidth={2} /> Requested
                            </>
                          ) : (
                            'Request'
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* =====================================================================
 * BOOK CLUBS: who you read with. Leads with the next meeting on the
 * calendar/date-block pattern (deliberately a different layout family
 * from the challenge cards below), then the clubs you belong to and
 * what each is reading. Non-members get an invitation instead; the
 * section hides entirely when no clubs exist.
 * ===================================================================== */

const clubQueryKeys = {
  clubs: ['student', 'desk', 'clubs'] as const,
  meetings: ['student', 'desk', 'club-meetings'] as const,
};

const timeLabel = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m ? `${hour}.${String(m).padStart(2, '0')}${period}` : `${hour}${period}`;
};

const ClubsSection: React.FC = () => {
  const qc = useQueryClient();
  const [joiningSlug, setJoiningSlug] = useState<string | null>(null);
  const [rsvpingId, setRsvpingId] = useState<number | null>(null);

  const { data: clubData, isLoading } = useQuery({
    queryKey: clubQueryKeys.clubs,
    queryFn: () => bookclubClubsApi.getClubs({ page_size: 50 }),
    staleTime: 5 * 60 * 1000,
  });
  const { data: meetingData } = useQuery({
    queryKey: clubQueryKeys.meetings,
    queryFn: () => bookclubMeetingsApi.getMeetings({ days: 30, page_size: 20 }),
    staleTime: 5 * 60 * 1000,
  });

  const clubs = clubData?.results ?? [];
  const mine = clubs.filter((c) => c.is_member);
  const mineSlugs = new Set(mine.map((c) => c.slug));
  /* Only meetings for clubs the reader actually belongs to. */
  const myMeetings = (meetingData?.results ?? [])
    .filter((m) => mineSlugs.has(m.club_slug))
    .slice(0, 3);

  const onJoin = async (slug: string, name: string) => {
    setJoiningSlug(slug);
    try {
      await bookclubMembershipApi.joinClub(slug);
      toast.success(`Joined ${name}`);
      qc.invalidateQueries({ queryKey: clubQueryKeys.clubs });
      qc.invalidateQueries({ queryKey: clubQueryKeys.meetings });
    } catch (e) {
      toast.error(apiError(e, 'Could not join this club'));
    } finally {
      setJoiningSlug(null);
    }
  };

  const onRsvp = async (meeting: ClubMeeting) => {
    const next: RSVPStatus = meeting.my_rsvp === 'attending' ? 'not_attending' : 'attending';
    setRsvpingId(meeting.id);
    try {
      await bookclubMeetingsApi.rsvp(meeting.id, next);
      toast.success(next === 'attending' ? 'You are going' : 'Marked as not going');
      qc.invalidateQueries({ queryKey: clubQueryKeys.meetings });
    } catch (e) {
      toast.error(apiError(e, 'Could not update your RSVP'));
    } finally {
      setRsvpingId(null);
    }
  };

  if (isLoading) {
    return (
      <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-14 md:pt-20">
        <div className="h-8 w-56 rounded bg-[var(--surface-2)] animate-pulse" />
        <div className="mt-8 space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-md bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (clubs.length === 0) return null;

  return (
    <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-14 md:pt-20">
      <Reveal>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="font-display font-[900] text-[1.8rem] md:text-[2.2rem] leading-[1.05] tracking-[-0.025em] text-[var(--ink)]">
            {mine.length > 0 ? 'Who you read with.' : 'Read with others.'}
          </h2>
          <Link
            to="/book-clubs"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
          >
            All book clubs <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
          </Link>
        </div>
      </Reveal>

      {mine.length === 0 ? (
        <Reveal delay={0.06}>
          <div className="mt-8 rounded-md border border-[var(--border)] bg-[var(--primary)]/8 p-6 md:p-7">
            <p className="text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[54ch]">
              You have not joined a book club yet. {clubs.length}{' '}
              {clubs.length === 1 ? 'club is' : 'clubs are'} running this term.
            </p>
            <div className="mt-5 space-y-3">
              {clubs.slice(0, 3).map((c) => (
                <div
                  key={c.id}
                  className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3 border-b border-[var(--border)] last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/book-clubs/${c.slug}`}
                      className="font-medium text-[14.5px] text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
                    >
                      {c.name}
                    </Link>
                    <div className="text-[12.5px] text-[var(--ink-soft)] truncate">
                      {c.current_book ? `Reading ${c.current_book.title}` : c.schedule_text}
                      {c.advisor_name ? ` · Advised by ${c.advisor_name}` : ''}
                    </div>
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--muted)]">
                    {c.member_count} {c.member_count === 1 ? 'member' : 'members'}
                  </span>
                  <button
                    onClick={() => onJoin(c.slug, c.name)}
                    disabled={joiningSlug === c.slug}
                    className={quietButton}
                  >
                    {joiningSlug === c.slug ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" />
                    ) : (
                      'Join'
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          {/* Next meetings: date-block timeline */}
          <Reveal className="lg:col-span-7">
            <div className="rounded-md border border-[var(--border)] bg-[var(--surface)] p-6 md:p-7">
              <PanelTitle>Next meetings</PanelTitle>
              {myMeetings.length === 0 ? (
                <p className="text-[14px] text-[var(--ink-soft)] py-1">
                  Nothing scheduled in the next month.
                </p>
              ) : (
                <div>
                  {myMeetings.map((m) => {
                    const d = new Date(`${m.date}T00:00:00`);
                    const going = m.my_rsvp === 'attending';
                    const busy = rsvpingId === m.id;
                    return (
                      <div
                        key={m.id}
                        className="flex gap-5 py-4 border-b border-[var(--border)] last:border-0 first:pt-0 last:pb-0"
                      >
                        <div className="shrink-0 text-center w-12">
                          <div className="font-display font-[900] text-[1.75rem] leading-none tracking-[-0.02em] text-[var(--ink)] tabular-nums">
                            {d.getDate()}
                          </div>
                          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">
                            {d.toLocaleDateString('en-GB', { month: 'short' })}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[14.5px] leading-snug text-[var(--ink)]">
                            {m.topic}
                          </div>
                          <div className="mt-1 text-[12.5px] text-[var(--ink-soft)] truncate">
                            {m.club_name}
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--muted)]">
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarClock className="w-3.5 h-3.5" strokeWidth={1.75} />
                              {timeLabel(m.start_time)}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5" strokeWidth={1.75} />
                              {m.location}
                            </span>
                            {(m.attending_count ?? 0) > 0 && (
                              <span>{m.attending_count} going</span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => onRsvp(m)}
                          disabled={busy}
                          className={
                            going
                              ? 'shrink-0 self-center inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--success)]/40 bg-[var(--success)]/10 text-[12.5px] font-medium text-[var(--success)] active:translate-y-px transition-all'
                              : `shrink-0 self-center ${quietButton}`
                          }
                        >
                          {busy ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : going ? (
                            <>
                              <Check className="w-3.5 h-3.5" strokeWidth={2} /> Going
                            </>
                          ) : (
                            'RSVP'
                          )}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Reveal>

          {/* Clubs you belong to */}
          <Reveal delay={0.08} className="lg:col-span-5">
            <div className="rounded-md border border-[var(--border)] bg-[var(--surface)] p-6 md:p-7">
              <PanelTitle
                meta={
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[var(--muted)]">
                    {mine.length} joined
                  </span>
                }
              >
                Your clubs
              </PanelTitle>
              <div>
                {mine.map((c) => (
                  <Link
                    key={c.id}
                    to={`/book-clubs/${c.slug}`}
                    className="group block py-3.5 border-b border-[var(--border)] last:border-0 first:pt-0 last:pb-0"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[14.5px] text-[var(--ink)] group-hover:text-[var(--primary)] transition-colors">
                        {c.name}
                      </span>
                      {c.my_role === 'leader' && (
                        <span className="shrink-0 text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[var(--gold)]/15 text-[var(--gold-text)]">
                          Leader
                        </span>
                      )}
                    </div>
                    {c.current_book ? (
                      <>
                        <div className="mt-1 text-[12.5px] text-[var(--ink-soft)] truncate">
                          Reading {c.current_book.title}
                        </div>
                        <div className="mt-2.5 h-[3px] rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-[var(--gold)]"
                            style={{ width: `${Math.min(100, c.current_book.progress)}%` }}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="mt-1 text-[12.5px] text-[var(--muted)]">
                        No book set yet
                      </div>
                    )}
                    {c.advisor_name && (
                      <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--muted)]">
                        Advised by {c.advisor_name}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      )}
    </section>
  );
};

/* =====================================================================
 * DISCUSSIONS: threads from the clubs you belong to. Rows expand in
 * place to read replies and answer, rather than opening a dialog:
 * Radix portals mount at document.body where the `.smchs-home`-scoped
 * CSS vars do not resolve. Posting is club-members-only server-side, so
 * this only lists threads from clubs the reader has joined.
 * ===================================================================== */

const DiscussionsSection: React.FC = () => {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<number | null>(null);
  const [composing, setComposing] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftClub, setDraftClub] = useState<number | null>(null);
  const [posting, setPosting] = useState(false);

  const { data: clubData } = useQuery({
    queryKey: clubQueryKeys.clubs,
    queryFn: () => bookclubClubsApi.getClubs({ page_size: 50 }),
    staleTime: 5 * 60 * 1000,
  });
  const { data: threadData, isLoading } = useQuery({
    queryKey: discussionKeys.list,
    queryFn: () =>
      bookclubDiscussionsApi.getDiscussions({ ordering: '-last_reply_at', page_size: 30 }),
    staleTime: 60 * 1000,
  });

  const mine = (clubData?.results ?? []).filter((c) => c.is_member);
  const mineSlugs = new Set(mine.map((c) => c.slug));
  const clubName = (slug: string) => mine.find((c) => c.slug === slug)?.name ?? slug;
  const threads = (threadData?.results ?? [])
    .filter((t) => mineSlugs.has(t.club_slug))
    .slice(0, 6);

  /* Nothing to discuss until they belong to a club; the clubs section
     above already carries the invitation to join one. */
  if (mine.length === 0) return null;

  const startThread = async () => {
    const club = draftClub ?? mine[0]?.id;
    if (!club || !draftTitle.trim()) return;
    setPosting(true);
    try {
      const res = await bookclubDiscussionsApi.createThread(club, draftTitle.trim());
      flaggedNotice(res, 'Thread started');
      setDraftTitle('');
      setComposing(false);
      qc.invalidateQueries({ queryKey: discussionKeys.list });
    } catch (e) {
      toast.error(apiError(e, 'Could not start that thread'));
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-14 md:pt-20">
      <Reveal>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="font-display font-[900] text-[1.8rem] md:text-[2.2rem] leading-[1.05] tracking-[-0.025em] text-[var(--ink)]">
            In discussion.
          </h2>
          <button
            onClick={() => setComposing((c) => !c)}
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
          >
            {composing ? 'Cancel' : 'Start a thread'}
          </button>
        </div>
      </Reveal>

      {composing && (
        <Reveal>
          <div className="mt-6 rounded-md border border-[var(--border)] bg-[var(--surface)] p-5 md:p-6">
            <label
              htmlFor="thread-title"
              className="block text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--muted)]"
            >
              Thread title
            </label>
            <input
              id="thread-title"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              placeholder="What do you want to talk about?"
              className="mt-2 w-full bg-transparent border-0 border-b border-[var(--border)] py-2.5 text-[1.05rem] text-[var(--ink)] placeholder:text-[var(--muted)] focus:border-[var(--gold)] focus:outline-none transition-colors"
            />
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {mine.length > 1 && (
                <>
                  <label
                    htmlFor="thread-club"
                    className="text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--muted)]"
                  >
                    In
                  </label>
                  <select
                    id="thread-club"
                    value={draftClub ?? mine[0].id}
                    onChange={(e) => setDraftClub(Number(e.target.value))}
                    className="rounded-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--ink)] px-3 py-1.5 text-[13px] focus:border-[var(--gold)] focus:outline-none"
                  >
                    {mine.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <button
                onClick={startThread}
                disabled={!draftTitle.trim() || posting}
                className={`ml-auto ${quietButton}`}
              >
                {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Post thread'}
              </button>
            </div>
          </div>
        </Reveal>
      )}

      {isLoading ? (
        <div className="mt-8 space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded-md bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      ) : threads.length === 0 ? (
        <Reveal delay={0.06}>
          <div className="mt-8 rounded-md border border-[var(--border)] bg-[var(--surface)] p-6 md:p-7">
            <p className="text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[50ch]">
              No one has started a thread in your clubs yet. Yours would be the first.
            </p>
          </div>
        </Reveal>
      ) : (
        <Reveal delay={0.06}>
          <div className="mt-8 rounded-md border border-[var(--border)] bg-[var(--surface)] overflow-hidden">
            {threads.map((t) => (
              <ThreadRow
                key={t.id}
                thread={t}
                clubName={clubName(t.club_slug)}
                open={openId === t.id}
                onToggle={() => setOpenId((id) => (id === t.id ? null : t.id))}
              />
            ))}
          </div>
        </Reveal>
      )}
    </section>
  );
};

const ThreadRow: React.FC<{
  thread: DiscussionThread;
  clubName: string;
  open: boolean;
  onToggle: () => void;
}> = ({ thread: t, clubName, open, onToggle }) => (
  <div className="border-b border-[var(--border)] last:border-0">
    <button
      onClick={onToggle}
      aria-expanded={open}
      className="w-full text-left px-5 md:px-6 py-4 flex items-start gap-4 hover:bg-[var(--surface-2)]/60 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-[15px] leading-snug text-[var(--ink)]">
            {t.title}
          </span>
          {t.is_pinned && (
            <span className="shrink-0 text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[var(--gold)]/15 text-[var(--gold-text)]">
              Pinned
            </span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--muted)]">
          <span>{clubName}</span>
          <span>{t.author_name}</span>
          <span>
            {t.reply_count} {t.reply_count === 1 ? 'reply' : 'replies'}
          </span>
          <span>{timeAgo(t.last_reply_at ?? t.created_at)}</span>
        </div>
      </div>
      <ChevronDown
        className={`w-4 h-4 mt-1 shrink-0 text-[var(--muted)] transition-transform ${
          open ? 'rotate-180' : ''
        }`}
        strokeWidth={1.75}
      />
    </button>
    {open && <ThreadBody threadId={t.id} />}
  </div>
);

const ThreadBody: React.FC<{ threadId: number }> = ({ threadId }) => {
  const { user } = useAuth();

  const { data, isLoading, isError } = useQuery({
    queryKey: discussionKeys.thread(threadId),
    queryFn: () => bookclubDiscussionsApi.getThread(threadId),
    staleTime: 30 * 1000,
  });

  return (
    <div className="px-5 md:px-6 pb-5 md:pb-6 bg-[var(--surface-2)]/40">
      {isLoading ? (
        <div className="space-y-3 pt-1">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 rounded bg-[var(--surface-2)] animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-[14px] text-[var(--danger)] pt-1">
          This thread would not load. Try again in a moment.
        </p>
      ) : (
        <>
          <div className="pt-1">
            <ReplyList
              threadId={threadId}
              replies={data?.replies ?? []}
              currentUserId={user?.id ?? null}
            />
          </div>
          <div className="mt-5">
            <ReplyComposer threadId={threadId} />
          </div>
          <Link
            to={`/book-clubs/threads/${threadId}`}
            className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[var(--ink-soft)] hover:text-[var(--primary)] transition-colors"
          >
            Open full thread <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
          </Link>
        </>
      )}
    </div>
  );
};

/* =====================================================================
 * CHALLENGES STRIP: enrolled progress, or a quiet invitation when the
 * reader has not joined anything. Hidden entirely when no challenges
 * are running.
 * ===================================================================== */

const ChallengesStrip: React.FC = () => {
  const { data: challenges } = useQuery({
    queryKey: ['challenges', 'list', 'desk'],
    queryFn: () => challengesApi.list(),
    staleTime: 5 * 60 * 1000,
  });

  const active = (challenges ?? []).filter((c) => c.status === 'active');
  const enrolled = active.filter((c) => c.is_enrolled);
  if (active.length === 0) return null;

  return (
    <section className="max-w-[1280px] mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-20 md:pb-24">
      <Reveal>
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="font-display font-[900] text-[1.8rem] md:text-[2.2rem] leading-[1.05] tracking-[-0.025em] text-[var(--ink)]">
            Reading challenges.
          </h2>
          <Link
            to="/challenges"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--ink)] hover:text-[var(--primary)] transition-colors"
          >
            All challenges <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.75} />
          </Link>
        </div>
      </Reveal>

      {enrolled.length === 0 ? (
        <Reveal delay={0.06}>
          <div className="mt-8 rounded-md border border-[var(--border)] bg-[var(--primary)]/8 p-6 md:p-7">
            <p className="text-[14.5px] leading-[1.6] text-[var(--ink-soft)] max-w-[52ch]">
              You have not joined a challenge yet. {active[0].name} is running now, with{' '}
              {active[0].days_left} {active[0].days_left === 1 ? 'day' : 'days'} left to enter.
            </p>
          </div>
        </Reveal>
      ) : (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          {enrolled.map((c, i) => (
            <ChallengeCard key={c.id} challenge={c} delay={i * 0.06} />
          ))}
        </div>
      )}
    </section>
  );
};

const ChallengeCard: React.FC<{ challenge: Challenge; delay?: number }> = ({
  challenge: c,
  delay,
}) => {
  const mine = c.my_progress ?? 0;
  const pct = c.target > 0 ? Math.min(100, (mine / c.target) * 100) : 0;
  return (
    <Reveal delay={delay}>
      <div className="h-full rounded-md border border-[var(--border)] bg-[var(--surface)] p-6 md:p-7">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-semibold text-[15px] text-[var(--ink)]">{c.name}</div>
            {c.reward && (
              <div className="mt-1 text-[12.5px] text-[var(--ink-soft)] line-clamp-1">
                {c.reward}
              </div>
            )}
          </div>
          <div className="shrink-0 font-display font-[900] text-[1.7rem] leading-none tracking-[-0.02em] tabular-nums text-[var(--ink)]">
            {mine}
            <span className="text-[var(--muted)] font-[700]">/{c.target}</span>
          </div>
        </div>
        <div className="mt-5 h-[3px] rounded-full bg-[var(--border)] overflow-hidden">
          <div
            className="h-full rounded-full bg-[var(--gold)] transition-[width] duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-2.5 flex items-center justify-between font-mono text-[10.5px] uppercase tracking-[0.16em] text-[var(--muted)]">
          <span>{c.unit}</span>
          <span>
            {c.days_left} {c.days_left === 1 ? 'day' : 'days'} left
          </span>
        </div>
      </div>
    </Reveal>
  );
};

export default StudentDesk;
